import { useCallback, useMemo, useState } from 'react';

/**
 * First-run "getting started" model (FR1/FR2). A pure function over the counts
 * the Dashboard already fetches — no extra network, trivially unit-testable —
 * modeled on `attention.ts`. Completion is **data-derived**: a step is done when
 * the underlying record exists, never because a row was clicked.
 */

/** Counts of each pillar's records in the active workspace. */
export interface FirstRunCounts {
  connections: number;
  entities: number;
  contacts: number;
  documents: number;
  /** Active FAMILY members in the workspace (≥1 — at least the viewer). A connected
   *  advisor's own membership row does not count toward this (`handlers/dashboard.ts`'s
   *  `isFamilyMember`, copy review 2026-09-21) — otherwise a family that has connected an
   *  advisor but invited nobody would read "Invite a family member" as already done. */
  members: number;
  /** Pending (not-yet-accepted) invitations. */
  invites: number;
  /**
   * Advisory firms with a live connection to this workspace (steward-app#1343 step 8a).
   *
   * ⚠️ **OPTIONAL, and absent means UNKNOWN — the step is still offered, and reads as NOT
   * DONE until a count arrives.** Every other count here comes from the dashboard summary
   * endpoint, which has no advisor field; this one is derived from `GET /advisor/connections`,
   * a second request that answers later than the summary and can fail or be disabled on its
   * own. So "unknown" is a state this checklist genuinely sits in, and it lasts as long as
   * that request does.
   *
   * ⚠️⚠️ **Absent used to mean "omit the row", and the change is deliberate.** Omitting it
   * kept the checklist honest at the cost of a DENOMINATOR THAT MOVED: the card painted five
   * steps and re-rendered as six the moment the advisor read landed, which is steward-app#510's
   * symptom on the one card the welcome tour anchors to. The two ways of holding that still
   * were both worse, and both were tried on this branch: waiting for the read before painting
   * loses the tour stop altogether when the read is slow (the tour waits about three seconds
   * and never revisits a step it passed), and assuming DONE while unknown would hide the card
   * and then pop it back in on an already-settled page.
   *
   * Assume-undone is the mild direction, and it is chosen for exactly that: its error is a row
   * that unticks itself once, a few hundred milliseconds after paint, on the way to being
   * right. Nothing else about it changes, and the card can only ever disappear (never appear)
   * as a result. `firstRunSteps` reads it exactly that way.
   */
  advisors?: number;
}

export interface FirstRunStep {
  key: string;
  label: string;
  /** Deep-link to the surface that satisfies this step. */
  href: string;
  done: boolean;
  /** Steps only an admin can act on (and only admins see). */
  adminOnly: boolean;
}

/**
 * Build the getting-started step list from live counts. Admin-only steps (invite)
 * are filtered out for non-admins, so the denominator ("X of N") is role-correct.
 *
 * Deliberately short: there are no "add an asset/account" or "add a liability"
 * rows. Connecting an institution produces both, so listing them sent people off
 * to hand-enter records a connection would have pulled in (a credit card typed in
 * by hand, for one). Manual entry still lives on the pillar pages for what no
 * connection can reach; the first-run list just doesn't point there.
 */
export function firstRunSteps(counts: FirstRunCounts, isAdmin: boolean): FirstRunStep[] {
  const all: FirstRunStep[] = [
    {
      key: 'connect',
      label: 'Connect a bank or brokerage',
      href: '/settings/connections',
      done: counts.connections > 0,
      adminOnly: false,
    },
    {
      key: 'entity',
      label: 'Define a legal entity (LLC or trust)',
      href: '/entities',
      done: counts.entities > 0,
      adminOnly: false,
    },
    {
      key: 'contact',
      label: 'Add a key contact',
      // Contacts folded into the People pillar as its second tab (steward-app#1425); the
      // page is unchanged, only its path moved. It is still reachable through standing
      // navigation (the People pillar's tab bar), which is what §3.3's "signposts are never
      // homes" rule requires of every href in this list.
      href: '/people/contacts',
      done: counts.contacts > 0,
      adminOnly: false,
    },
    {
      key: 'document',
      label: 'Upload a document',
      href: '/documents',
      done: counts.documents > 0,
      adminOnly: false,
    },
    {
      key: 'invite',
      label: 'Invite a family member',
      href: '/people/access',
      // Done once someone else is in (≥2 members) or an invite is outstanding.
      done: counts.members >= 2 || counts.invites >= 1,
      adminOnly: true,
    },
  ];
  /**
   * "Connect your advisor" (steward-app#1343 §Surfaces item 5).
   *
   * The design's reason for it: a client who arrives because their advisor sent them has no
   * other way to find this surface, and a checklist that never mentions advisors makes the
   * whole channel discoverable only to somebody already looking for it.
   *
   * ⚠️ **`adminOnly`, because `/people/advisors` is behind `RequireAdmin`**
   * (`src/router.tsx`). A row a non-admin can see but not act on would deep-link them into a
   * refusal, and it would also make the "X of N" denominator wrong for them, which is the one
   * thing `firstRunSteps`' own filter exists to keep right.
   *
   * ⚠️ **Pushed UNCONDITIONALLY, and `done` is false while the count is unknown.** See
   * `FirstRunCounts.advisors` for the whole argument; the short form is that the row's
   * presence is what fixes the denominator, so it cannot be conditional on an answer that
   * arrives after first paint. `counts.advisors ?? 0` is the assume-undone rule, written once,
   * here.
   *
   * ⚠️⚠️ **This does put a briefly false row in front of a family who HAS an advisor**, and
   * on a checklist that is a real cost: the row says "Connect your advisor" to somebody who
   * already has. It is accepted because it is bounded by one request and self-correcting, and
   * because the alternatives are not: a row that appears late moves the "X of N" a family is
   * reading, and a card that is not on screen when the welcome tour opens loses its stop for
   * the whole session. A caller whose advisor read never succeeds leaves the card at "N-1 of
   * N" and so never auto-hides, which is the one lasting cost of this choice and is preferred
   * to hiding the five steps that are known good.
   *
   * `/people/advisors` carries a SECOND route guard, `RequireFamilyWorkspace`
   * (steward-app#798), and this step deliberately does not reproduce its predicate.
   *
   * ⚠️ **The reason is that the two guards are COMPLEMENTS of one expression, not that
   * `Dashboard` never renders in a firm workspace.** An earlier version of this paragraph
   * claimed the latter, and it is false as written: `HomeRoute` renders `<Dashboard />` on
   * every path where `workspaceKind !== 'firm'` and the caller is unplaced, and a firm
   * workspace can be provisioned without the `steward-advisor-firm` pack ever mounting, which
   * is exactly what steward-app#736 is open on. So this is a false invariant rather than a
   * point-in-time reading, and no re-reading of the source would have expired it.
   *
   * What actually makes the row safe is durable and checkable in one line each: both
   * `RequireFamilyWorkspace` and `HomeRoute` now call the SAME shared `isFirmWorkspace`
   * helper (`workspace/useWorkspaceKind.ts`, steward-app#736, 2026-09-23 — replacing the two
   * inline copies of the expression this paragraph used to quote directly, which is what made
   * it worth restating here at all; a single named function is not). `HomeRoute` reaches
   * `<Dashboard />` only when `workspaceKind !== 'firm'` AND its own (wider, `placedAtSomeFirm`
   * -gated) call to that helper is false; by the time `RequireFamilyWorkspace` evaluates the
   * SAME helper against the SAME `activeMembership`/`activeWorkspaceId`/`advisorFor`, it
   * reduces to that same `workspaceKind !== 'firm'` fact once membership is known (its own
   * `if (!activeMembership) return null` guard means it never reaches the helper's
   * loading-window stand-in at all). Wherever `HomeRoute` shows this checklist with a
   * resolved membership, therefore, `/people/advisors` admits the person it sends there. A
   * third copy of the predicate here would buy nothing, and `RequireFamilyWorkspace`'s own
   * docblock warns against exactly that.
   */
  all.push({
    key: 'advisor',
    label: 'Connect your advisor',
    // People → Advisors since steward-app#1922 (was `/settings/advisors`, now a redirect).
    href: '/people/advisors',
    done: (counts.advisors ?? 0) > 0,
    adminOnly: true,
  });
  return all.filter(s => isAdmin || !s.adminOnly);
}

export interface FirstRunProgress {
  done: number;
  total: number;
  isComplete: boolean;
}

export function firstRunProgress(steps: FirstRunStep[]): FirstRunProgress {
  const done = steps.filter(s => s.done).length;
  const total = steps.length;
  return { done, total, isComplete: total > 0 && done === total };
}

// ── Per-user, per-workspace dismissal (FR2) ────────────────────────────────
// Persisted server-side on the user's profile (preferences.firstRunDismissed via
// /me), so a dismissal follows the user across devices. The hook is handed the
// current dismissed flag for the active workspace plus a persist callback — it owns
// no storage itself (WorkspaceProvider does the optimistic cache update + PUT).

/**
 * Whether the checklist card should render. Once every step is complete, a plain
 * load treats it as an implicit dismissal (no nagging "you're all set" banner
 * forever) — reopening via the account menu still shows it once, via forceOpen.
 * Pure so the transition is unit-testable without a React/DOM test setup.
 */
export function shouldShowFirstRun({ ready, canPersist, forceOpen, storedDismissed, isComplete }: {
  ready: boolean;
  canPersist: boolean;
  forceOpen: boolean;
  storedDismissed: boolean;
  isComplete: boolean;
}): boolean {
  return ready && canPersist && (forceOpen || (!storedDismissed && !isComplete));
}

export interface UseFirstRunArgs {
  counts: FirstRunCounts;
  isAdmin: boolean;
  workspaceId: string | null;
  /** True once the underlying count queries have settled (not loading/errored). */
  ready: boolean;
  /** Whether the user has dismissed the checklist for this workspace (from their
   *  cross-device profile preferences). */
  dismissed: boolean;
  /** Persist a dismissal for the active workspace (optimistic + cross-device). */
  onDismiss: () => void;
}

export interface UseFirstRunResult extends FirstRunProgress {
  steps: FirstRunStep[];
  /** Whether the checklist card should render. */
  show: boolean;
  dismiss: () => void;
  /** Re-open for the current session even if previously dismissed (FR4 re-entry). */
  reopen: () => void;
}

/**
 * Owns first-run visibility for the Dashboard card: derives the step list +
 * progress from counts, and reads/writes the per-user-per-workspace dismissal.
 * Never blocks the app — if identity/workspace is unknown or queries haven't
 * settled, `show` is false and nothing renders.
 */
export function useFirstRun({ counts, isAdmin, workspaceId, ready, dismissed, onDismiss }: UseFirstRunArgs): UseFirstRunResult {
  const steps = useMemo(() => firstRunSteps(counts, isAdmin), [counts, isAdmin]);
  const progress = useMemo(() => firstRunProgress(steps), [steps]);

  // forceOpen is a session override set by reopen() (account-menu re-entry). The
  // durable dismissed state comes from the caller (profile preferences), so the
  // card hides immediately when onDismiss's optimistic update flips `dismissed`.
  const [forceOpen, setForceOpen] = useState(false);

  const dismiss = useCallback(() => {
    setForceOpen(false);
    onDismiss();
  }, [onDismiss]);

  const reopen = useCallback(() => setForceOpen(true), []);

  const show = shouldShowFirstRun({ ready, canPersist: !!workspaceId, forceOpen, storedDismissed: dismissed, isComplete: progress.isComplete });

  return { ...progress, steps, show, dismiss, reopen };
}
